import { useCallback, useEffect, useMemo, useState } from 'react';
import { useSelector } from 'react-redux';
import { featureToggleSelectors } from '@makemydeal/dr-shared-store';
import type { RetentionDecisions } from '../../types';
import { buildRetentionDecisions as buildDecisions, selectCategories } from './retentionDecisions';

export const useItemRetentionSelections = (show: boolean) => {
    const categories = useSelector(selectCategories);
    const isPercentTaxRatesEnabled = useSelector(featureToggleSelectors.isPercentTaxRatesEnabled);

    const buildInitialSelections = useCallback(() => {
        const initial: Record<string, Record<string, boolean>> = {};
        categories.forEach((category) => {
            initial[category.key] = {};
            category.items.forEach((item) => {
                initial[category.key][item.id] = false;
            });
        });
        return initial;
    }, [categories]);

    const [selections, setSelections] = useState<Record<string, Record<string, boolean>>>(buildInitialSelections);

    // Reset selections when modal opens/closes
    useEffect(() => {
        setSelections(buildInitialSelections());
    }, [show]);

    const allChecked = useMemo(() => {
        return Object.values(selections).every((categoryItems) => Object.values(categoryItems).every((checked) => checked));
    }, [selections]);

    const handleCheckboxChange = useCallback((categoryKey: string, itemId: string, checked: boolean) => {
        setSelections((prev) => ({
            ...prev,
            [categoryKey]: {
                ...prev[categoryKey],
                [itemId]: checked
            }
        }));
    }, []);

    const handleKeepAllToggle = useCallback(() => {
        const newValue = !allChecked;
        setSelections((prev) => {
            const next: Record<string, Record<string, boolean>> = {};
            Object.keys(prev).forEach((categoryKey) => {
                next[categoryKey] = {};
                Object.keys(prev[categoryKey]).forEach((itemId) => {
                    next[categoryKey][itemId] = newValue;
                });
            });
            return next;
        });
    }, [allChecked]);

    const buildRetentionDecisions = useCallback(
        (): RetentionDecisions =>
            buildDecisions(categories, (categoryKey, itemId) => selections[categoryKey][itemId] === true, isPercentTaxRatesEnabled),
        [categories, selections, isPercentTaxRatesEnabled]
    );

    return {
        categories,
        selections,
        allChecked,
        handleCheckboxChange,
        handleKeepAllToggle,
        buildRetentionDecisions
    };
};
